import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { OrganisationDto } from '@treetop/shared/model';

import { getOrganisationImage } from './OrganisationImageHelper';
import { UserService } from './user.service';

export type OrganisationNode = {
  id: string;
  name: string;
  parentId?: string;
  children?: OrganisationNode[];
};

export type RootOrganisationNode = OrganisationNode & {
  initials: string;
  organisationImage: string;
};

@Injectable({ providedIn: 'root' })
export class OrganisationTreeService {
  private userService = inject(UserService);

  private currentUser = toSignal(this.userService.currentUser$);

  private organisations = computed(() => {
    return this.currentUser()?.organisations || [];
  });

  public readonly rootOrganisations = computed(() => {
    const tree = this.organisationTree();
    return tree
      .filter((node) => !node.parentId)
      .map((node) => ({
        ...node,
        initials: `${node.name.charAt(0).toUpperCase()}`,
        organisationImage: `${getOrganisationImage(node.id)}`,
      }));
  });

  public organisationTree = computed(() => {
    const organisations = this.organisations();
    return this.buildOrganisationTreeFromDto(organisations);
  });

  public findNodeById(nodes: OrganisationNode[], id: string): OrganisationNode | undefined {
    for (const node of nodes) {
      if (node.id === id) return node;

      if (node.children) {
        const found = this.findNodeById(node.children, id);

        if (found) return found;
      }
    }

    return undefined;
  }

  /**
   * Returns a flattened array of the parent-most ancestor and all its descendants,
   * given any node id in the organisation tree.
   * If the node is not found, returns an empty array.
   *
   * @param id - The id of the node to find the branch for.
   * @returns OrganisationNode[] - Flattened array from parent-most ancestor to all descendants.
   */
  public getFlattenedBranchByNodeId(id: string): OrganisationNode[] {
    const tree = this.organisationTree();

    let targetNode = this.findNodeById(tree, id);
    if (!targetNode) return [];

    while (targetNode.parentId) {
      const parent = this.findNodeById(tree, targetNode.parentId);
      if (!parent) break;
      targetNode = parent;
    }

    const result: OrganisationNode[] = [];
    function flatten(node: OrganisationNode) {
      result.push(node);
      if (node.children) {
        for (const child of node.children) {
          flatten(child);
        }
      }
    }
    flatten(targetNode);

    return result;
  }

  /**
   * Builds a hierarchical organisation tree from a flat array of OrganisationNode objects.
   * Each node is mapped by its id, and child nodes are assigned to their parent's `children` array.
   * Nodes without a valid parentId become root nodes.
   *
   * @param orgs - Flat array of OrganisationNode objects, each with an id and optional parentId.
   * @returns Array of root OrganisationNode objects, each with nested children forming the tree.
   */
  private buildOrganisationTree(orgs: OrganisationNode[]): OrganisationNode[] {
    const idToNodeMap = new Map<string, OrganisationNode>();
    const roots: OrganisationNode[] = [];

    orgs.forEach((org) => {
      idToNodeMap.set(org.id, { ...org, children: [] });
    });

    idToNodeMap.forEach((node) => {
      if (node.parentId && idToNodeMap.has(node.parentId)) {
        const parent = idToNodeMap.get(node.parentId);
        if (parent && parent.children) {
          parent.children.push(node);
        }
      } else {
        roots.push(node);
      }
    });

    return roots;
  }

  private buildOrganisationTreeFromDto(orgs: OrganisationDto[]): OrganisationNode[] {
    const nodes = this.convertDtoToOrganisationNodes(orgs);
    return this.buildOrganisationTree(nodes);
  }

  private convertDtoToOrganisationNodes(orgs: OrganisationDto[]): Partial<OrganisationNode[]> {
    return orgs.map((org) => ({
      id: org.id,
      name: org.displayName,
      parentId: org.parentId,
    }));
  }
}
